// mwtNav.js — the PURE multi-workspace-tabs navigation decision (Phase 3).
// Split out of the router wrapper so it imports nothing but the sibling path
// helpers (no `@symbo.ls/router`) and stays unit-testable in isolation. Both the
// `context.functions.router` wrapper (mwtRouter.js) and any consumer that needs
// to compute the prefixed URL for an intranet target import from here.

import {
  stripWorkspacePrefix,
  withWorkspacePath,
  withWorkspaceQuery,
  getWorkspacePrefixSlug,
  getWorkspacePathHandle,
  getWorkspaceQueryParam
} from './workspacePrefix.js'
import { isModuleRoute } from './moduleRoutes.js'

// Authenticated intranet routes that carry the `/w/<org-slug>` prefix. Excludes
// auth/public routes (login/signup/auth callbacks, user/u, meet-guest) + canvas/
// marketplace/preview (their own manifests) — bare `/studio` is the one
// exception, see the STUDIO-ORG-DESYNC-1 note below. Home `/` IS prefixed
// (→ /w/<slug>).
// `tasks`/`task` joined at the tickets→tasks rename (2026-08-06); the legacy
// `tickets`/`ticket` forms stay listed so inbound old links keep their prefix
// through the tasks-redirect hop. `resolutions` was a silent miss ever since
// that group moved out of the tickets sidebar — its clicks dropped the
// prefix. Longer alternatives first (tasks before task) — the trailing
// (\/|$) makes order mostly moot, but keep it disciplined.
// FA204/FA207 / frank trap — `INTRANET_RE` (module-scope) and
// `isMwtIntranetRoute` (a sibling export read bare by planMwtNav below) are
// both unresolvable once this file's exports are individually
// frank.toJSON'd for platform delivery (project_frank_drops_module_local_helpers;
// readerVoice.js precedent). `isMwtIntranetRoute` carries its own local copy
// of the regex; `planMwtNav` carries a full self-contained duplicate of this
// function's logic instead of calling it bare.
//
// `moduleRoutes` are the paths an installed workspace module/app injected at
// runtime (`/exams`, `/manage/members`, …). They cannot be part of the static
// list — they arrive with whatever the workspace has installed — but they ARE
// intranet: without them an in-app click dropped BOTH the `/w/<org>` prefix and
// the `?ws=` handle, so the very surfaces a workspace installs were the ones
// that lost their workspace identity on the next reload.
//
// STUDIO-ORG-DESYNC-1 (2026-08-15, Nika ruling) — nine routes were never
// classified either way (`analytics`, `workspace-analytics`, `bookmarks`,
// `files`, `agents`, `my-agents`, `reminders`, `team-and-agents`, `add-app`);
// added below. `/studio` (BARE ONLY — see the isBareStudio note) joins the
// intranet set too: it is the signed-in user's OWN canvas board, tab-scoped
// like every other intranet surface, and excluding it let scope fall back to
// a stale `localStorage.activeWorkspace` read instead of the URL's org — the
// five-region disagreement the ticket reports. `/canvas` and `/marketplace`
// are DELIBERATELY UNCHANGED — outside this ruling's scope, pinned by a test.
export const isMwtIntranetRoute = (p, moduleRoutes) => {
  const INTRANET_RE =
    /^\/(tasks|task|tickets|ticket|resolutions|chat|chats|calendar|inbox|epics|teams|team-and-agents|meet|notes|documents|docs|admin|people|my-agents|my|announcements|company|settings|profile|data|system-status|integrations|features|analytics|workspace-analytics|bookmarks|files|agents|reminders|history|add-app|infra|apps|getting-started)(\/|$)/
  // Bare `/studio` (or `/studio/`) only — NEVER a `/studio/<owner>` sub-path.
  // That shape names a (possibly foreign) org in path segment 2 (app.js
  // `_urlOrgSlug`/`ownerSlugForPath`) and is handled by the cross-org
  // reconcile branch instead of the org-prefix canonicalize branch. Matching
  // it here too would stamp THIS tab's ambient org/workspace onto a URL
  // naming a DIFFERENT org's board — exactly the foreign-handle poisoning
  // class workspaceUrlSync.js's header documents for `/natali/shop?ws=server`
  // (project-canvas paths), reproduced one level up for studio owner paths.
  const isBareStudio = /^\/studio\/?$/.test(p)
  return p === '/' || isBareStudio || INTRANET_RE.test(p) || isModuleRoute(p, moduleRoutes)
}

// PURE decision: given a nav path + the active org slug, compute the match path
// (stripped) + the address-bar URL (prefixed for intranet). Returns null when the
// nav should pass through UNCHANGED — MWT off. When on but the route is
// non-intranet or no slug is known, `addressBarUrl` is null (strip only, no
// prefix). When on + intranet + slug, `addressBarUrl` is the `/w/<slug>` URL.
// Cross-org reconcile decision (Phase 5): given the URL's `/w/<slug>`, the orgs
// list, and the currently-active org id, return the org id this tab should
// client-only switch to (`sdk.switchOrg(id, {skipPersist:true})`), or null.
// Null when: no slug, unknown slug, active org not yet resolved, or already on
// the URL's org. This is how a deep-linked/reloaded `/w/<other-org>/…` tab loads
// the org named in its URL (its workspaces aren't resolvable pre-mount) without
// mutating the server-side global active org or broadcasting to other tabs.
export const mwtOrgToReconcile = ({ slug, orgs, activeOrgId }) => {
  if (!slug || !activeOrgId) return null
  const org = (Array.isArray(orgs) ? orgs : []).find((o) => o && o.slug === slug)
  if (!org) return null
  const id = String(org.id ?? org._id ?? '')
  return id && id !== String(activeOrgId) ? id : null
}

// `ws` is the active workspace's URL handle, passed ONLY when the workspace
// needs one (resolveWorkspaceUrlHandle's rule — non-HQ always, HQ never).
// Scheme A: the handle is emitted as PATH SEGMENT 2 — `/w/<org>/<ws>/<route>` —
// because the prefix alone names just the org: without it the very first
// in-app click would strip the disambiguator off the address bar and the next
// reload would fall back to the org's HQ — the same wrong-workspace bug, one
// navigation later.
export const planMwtNav = ({ path, slug, ws, moduleRoutes, mwtEnabled }) => {
  if (!mwtEnabled) return null
  const raw = typeof path === 'string' ? path : '/'
  const matchPath = stripWorkspacePrefix(raw)
  const rawSlug = getWorkspacePrefixSlug(raw)
  // If the caller ALREADY prefixed the path (e.g. the switcher's cross-org
  // rewrite emits `/w/<newSlug>/…`), trust that slug verbatim — do NOT
  // re-derive from `root.org.slug`, which can still be the OLD org mid-switch.
  // Cross-ORG targets pass through UNTOUCHED: this org's ambient handle (and
  // even an explicit `?ws=`) is org-scoped, so carrying it over would name a
  // workspace that does not exist in the destination org.
  if (rawSlug && rawSlug !== slug) return { matchPath, addressBarUrl: raw }
  // WHICH workspace, by precedence: a handle the caller already put IN THE
  // PATH (`/w/org/<handle>/…`) → an explicit legacy `?ws=` (cross-workspace
  // deep link, parsed forever) → the tab's ambient active workspace.
  const explicit = getWorkspaceQueryParam(raw)
  const handle = getWorkspacePathHandle(raw) || explicit || ws || null
  if (!rawSlug) {
    // Self-contained duplicate of isMwtIntranetRoute's logic (FA204/FA207,
    // see the file-header note) — keep both copies in sync, incl. the
    // bare-`/studio`-only carve-out (STUDIO-ORG-DESYNC-1).
    const INTRANET_RE =
      /^\/(tasks|task|tickets|ticket|resolutions|chat|chats|calendar|inbox|epics|teams|team-and-agents|meet|notes|documents|docs|admin|people|my-agents|my|announcements|company|settings|profile|data|system-status|integrations|features|analytics|workspace-analytics|bookmarks|files|agents|reminders|history|add-app|infra|apps|getting-started)(\/|$)/
    const pathOnly = matchPath.split(/[?#]/)[0]
    const isBareStudio = /^\/studio\/?$/.test(pathOnly)
    const isIntranet =
      pathOnly === '/' ||
      isBareStudio ||
      INTRANET_RE.test(pathOnly) ||
      isModuleRoute(pathOnly, moduleRoutes)
    if (!slug || !isIntranet) return { matchPath, addressBarUrl: null }
  }
  // Emitted URLs are canonical scheme A: handle in the path, `?ws=` stripped.
  const route = explicit ? withWorkspaceQuery(matchPath, null) : matchPath
  return { matchPath, addressBarUrl: withWorkspacePath(rawSlug || slug, handle, route) }
}
